import type { IResource } from "@gettactic/api";
import { InteractiveZone } from "@gettactic/helpers/src/map/types";
import type { IResourceWithSchedules } from "@gettactic/hooks";
import React from "react";
import { ResourcesDetailsOverview } from "@/components/map/ResourcesDetailsOverview";
import { createPortal } from "react-dom";
import { usePopper } from "react-popper";
import { useMap } from "@webV2/map/MapProvider";

type MapViewHoverDetailsProps = {
  resource: IResourceWithSchedules | null;
  setResource: React.Dispatch<React.SetStateAction<IResource | null>>;
  zones: InteractiveZone[];
  floor: { name: string } | null;
  openReservation: (res: IResource) => void;
  setPopperElement: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  formattedDate: string;
  enablePortal?: boolean;
  highlightUserId?: string | undefined;
  mapContainerRef: React.RefObject<HTMLDivElement>;
};

export default function MapViewHoverDetails({
  resource,
  floor,
  formattedDate,
  enablePortal = false,
  highlightUserId,
  mapContainerRef
}: MapViewHoverDetailsProps) {
  // Popper styling instructions for resource details hover

  const { events } = useMap();
  const [popperElement, setPopperElement] = React.useState<HTMLElement | null>(
    null
  );

  const [hoveringElement, setHoveringElement] =
    React.useState<HTMLElement | null>(null);

  React.useEffect(() => {
    const highlightResource = (resource: IResource | null) => {
      if (!resource) {
        setHoveringElement(null);

        return;
      }
      const hoveringElement = (
        mapContainerRef.current || document
      ).querySelector<HTMLDivElement>(`#interactive-${resource.id}`);
      setHoveringElement(hoveringElement);
    };
    events.register("onHighlightResource", highlightResource);

    return () => events.unregister("onHighlightResource", highlightResource);
  }, [!!mapContainerRef.current]);

  const {
    styles: stylesDetailsHighlight,
    attributes: attributesDetailsHighlight
  } = usePopper(hoveringElement, popperElement, {
    placement: "auto",
    strategy: "fixed",
    modifiers: [
      {
        name: "offset",
        enabled: true,
        options: {
          offset: [10, 10]
        }
      }
    ]
  });

  if (!resource) {
    return null;
  }

  const content = (
    <div
      ref={setPopperElement}
      style={stylesDetailsHighlight.popper}
      {...attributesDetailsHighlight}
      className="absolute z-50 hidden max-w-md flex-1 rounded-md border border-gray-200 bg-white p-2 px-2 sm:px-4 md:block [.tactic-v2_&]:z-popover"
    >
      <ResourcesDetailsOverview
        resourceWithSchedules={resource}
        formattedDate={formattedDate}
        floor={floor}
        highlightUserId={highlightUserId}
      />
    </div>
  );

  if (enablePortal) {
    createPortal(content, document.body);
  }

  return content;
}
